import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Card, Button, Space, Divider, message } from 'antd';
import pageS from '../../s.module.less';
import s from './s.module.less';
import BillingPoliciesStep from '../BillingPoliciesStep';
import ConsentFormsStep from '../ConsentFormsStep';
import PracticeProfileStep from '../PracticeProfileStep';
import BusinessInfoStep from '../BusinessInfoStep';
import PayoutAccountStep from '../PayoutAccountStep';
import BillPayoutSetupModal from 'pages/PPAAdvanceSettingPage/components/ReviewSettings/components/BillPayoutSetupModal';
import BillingPayoutReadOnly from 'components/BillingPayoutReadOnly';
import WarningTips from 'components/WarningTips';
import { BusinessType } from 'types/paymentSetup';
import copy from 'copy-to-clipboard';
import useProviderOverview from 'hooks/useProviderOverview';
import { isDevEnv, isTestEnv } from 'utils/common';
import { getProviderBillPayoutDetail, submitProviderBillPayoutToBill } from 'api/appointmentSetting';
import type { ProviderBillPayoutDetailVo } from 'types/providerBill';

type Props = {
    type?: 'onboarding' | 'payment';
};

const ReviewStep: React.FC = forwardRef((props: Props, ref) => {
    const { type = 'payment' } = props;
    const [editingSection, setEditingSection] = useState<number | null>(null);
    const billingPoliciesRef = useRef<any>(null);
    const consentFormsRef = useRef<any>(null);
    const practiceAddressRef = useRef<any>(null);
    const practiceProfileRef = useRef<any>(null);
    const businessInfoRef = useRef<any>(null);
    const payoutAccountRef = useRef<any>(null);
    const [provider] = useProviderOverview();
    const useStripePayoutUi = provider?.hasStripeConnectAccount === true;
    const [billPayoutData, setBillPayoutData] = useState<ProviderBillPayoutDetailVo | undefined>();
    const [billPayoutModalOpen, setBillPayoutModalOpen] = useState(false);

    useEffect(() => {
        if (useStripePayoutUi) return;
        getProviderBillPayoutDetail().then((res) => {
            if (!res.error) setBillPayoutData(res?.data?.data);
        });
    }, [useStripePayoutUi]);

    const steps = [
        {
            id: 1,
            title: 'Appointment billing policies',
            component: <BillingPoliciesStep ref={billingPoliciesRef} isReview isEdit={editingSection === 1} />,
        },
        {
            id: 2,
            title: 'Practice profile',
            component: (
                <PracticeProfileStep
                    ref={practiceProfileRef}
                    isReview
                    isEdit={editingSection === 2}
                    showPracticeAddress={false}
                />
            ),
        },
        {
            id: 3,
            title: 'Consent forms',
            component: <ConsentFormsStep ref={consentFormsRef} isReview isEdit={editingSection === 3} />,
        },
        {
            id: 4,
            title: 'Practice Address',
            component: (
                <PracticeProfileStep
                    ref={practiceAddressRef}
                    isReview
                    isEdit={editingSection === 4}
                    showPracticeForm={false}
                />
            ),
        },
        ...(useStripePayoutUi
            ? [
                  {
                      id: 5,
                      title: 'Business information and Business identity verification',
                      component: <BusinessInfoStep ref={businessInfoRef} isReview isEdit={editingSection === 5} />,
                  },
              ]
            : []),
        {
            id: 6,
            title: 'Payout account',
            component: useStripePayoutUi ? (
                <PayoutAccountStep ref={payoutAccountRef} isReview isEdit={editingSection === 6} />
            ) : (
                <BillingPayoutReadOnly data={billPayoutData} />
            ),
        },
    ];

    const handleCancel = () => {
        setEditingSection(null);
    };

    const handleSave = async (sectionId: number) => {
        try {
            switch (sectionId) {
                case 1:
                    await billingPoliciesRef.current?.onSubmit();
                    break;
                case 2:
                    await practiceProfileRef.current?.onSubmit();
                    break;
                case 3:
                    await consentFormsRef.current?.onSubmit();
                    break;
                case 4:
                    await practiceAddressRef.current?.onSubmit();
                    break;
                case 5:
                    await businessInfoRef.current?.onSubmit();
                    break;
                case 6:
                    await payoutAccountRef.current?.onSubmit();
                    break;
                default:
                    setEditingSection(null);
                    break;
            }
            setEditingSection(null);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSubmit = async () => {
        if (useStripePayoutUi) {
            const businessType = businessInfoRef.current?.getBusinessType?.();
            if (!businessType) {
                message.error('Business type is required. Please select a specific type to submit.');
                throw new Error('Please select a valid business type');
            }
            if (businessType === BusinessType.OTHERS) {
                message.error(
                    'Business type cannot be "Others" at this time. Please select a specific type to submit.',
                );
                throw new Error('Business type error');
            }
        }
        if (!useStripePayoutUi) {
            await submitProviderBillPayoutToBill();
        }
    };

    const copyKlarityLink = () => {
        const KlarityDomain = isDevEnv()
            ? 'https://dev.d2ysjydukc4efk.amplifyapp.com'
            : isTestEnv()
              ? 'https://test.d2ysjydukc4efk.amplifyapp.com'
              : 'https://helloklarity.com';
        const pppUrl = `${KlarityDomain}/provider/${provider.providerNameSlug}`;
        copy(pppUrl);
        message.success('Klarity link copied to clipboard');
    };

    useImperativeHandle(ref, () => ({
        onSubmit: handleSubmit,
    }));

    const renderEditButton = (sectionId: number) => (
        <Button onClick={() => setEditingSection(sectionId)} className={s.editButton}>
            Edit
        </Button>
    );

    const renderActionButtons = (sectionId: number) => (
        <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" onClick={() => handleSave(sectionId)} className={s.saveButton}>
                Save changes
            </Button>
        </Space>
    );

    return (
        <div className={`${s.reviewStep} ${type === 'onboarding' ? s.onboardingReviewStep : ''}`}>
            <div className={s.header}>
                <h1 className={pageS.title}>
                    {type === 'onboarding' ? 'Review your no-show policy settings' : 'Review your setup'}
                </h1>
                <p className={pageS.desc}>
                    Please confirm your practice details before moving forward. You&rsquo;ll be able to make changes
                    later if needed.
                </p>
            </div>

            <WarningTips
                title="Reminder: Please make sure your Stripe account and payment setup use your Klarity Payment Portal link, not your personal or clinic website. Using external links may cause Stripe payment issues or account suspension."
                content={
                    <Button className={s.copyButton} onClick={copyKlarityLink}>
                        Copy my Klarity link
                    </Button>
                }
            />

            <div className={s.sections}>
                {steps.map((step) => (
                    <Card
                        key={step.id}
                        title={step.title}
                        extra={
                            step.id === 6 && !useStripePayoutUi ? (
                                <Button onClick={() => setBillPayoutModalOpen(true)} className={s.editButton}>
                                    Edit
                                </Button>
                            ) : editingSection === step.id ? (
                                renderActionButtons(step.id)
                            ) : (
                                renderEditButton(step.id)
                            )
                        }
                        className={s.sectionCard}
                        bordered={false}
                    >
                        <Divider style={{ marginTop: '8px' }} />
                        {step.component}
                    </Card>
                ))}
            </div>

            {!useStripePayoutUi && (
                <BillPayoutSetupModal
                    open={billPayoutModalOpen}
                    onClose={() => setBillPayoutModalOpen(false)}
                    initialValue={billPayoutData}
                    onSaved={() => {
                        setBillPayoutModalOpen(false);
                        getProviderBillPayoutDetail().then((res) => {
                            if (!res.error) setBillPayoutData(res?.data?.data);
                        });
                    }}
                />
            )}
        </div>
    );
});

export default ReviewStep;
